import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import supportService from '@/services/supportService';
import { TicketStatus } from '@/types';
import toast from 'react-hot-toast';

interface TicketDetailContentProps {
  id: string;
}

const TicketDetailContent: React.FC<TicketDetailContentProps> = ({ id }) => {
  const queryClient = useQueryClient();
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

  const { data: ticket, isLoading, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => supportService.getTicketDetail(id),
    enabled: !!id,
  });

  const closeMutation = useMutation({
    mutationFn: () => supportService.closeTicket(id),
    onSuccess: () => {
      toast.success('Ticket fermé');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setIsCloseModalOpen(false);
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 text-center">
        <MaterialIcon icon="error_outline" className="text-4xl text-gray-400" />
        <p className="text-gray-500">Impossible de charger le ticket</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="bg-white dark:bg-surface-dark p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-white/5">
        <h2 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">{ticket.subject}</h2>
        <span className="inline-block px-2 py-1 bg-gray-100 dark:bg-white/10 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 mb-4 capitalize">
          {ticket.category} • {ticket.priority}
        </span>
        <p className="text-gray-600 dark:text-gray-300 text-sm whitespace-pre-wrap">
          {ticket.description}
        </p>

        {ticket.attachments && ticket.attachments.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
            <h3 className="text-sm font-medium mb-2">Pièces jointes</h3>
            <ul className="flex flex-col gap-2">
              {ticket.attachments.map((att) => (
                <li key={att.id}>
                  <a
                    href={att.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <MaterialIcon icon="attach_file" className="text-gray-400" />
                    <span className="text-sm text-primary underline truncate">{att.fileName}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {ticket.status !== TicketStatus.CLOSED && (
        <button
          type="button"
          onClick={() => setIsCloseModalOpen(true)}
          className="w-full py-3 rounded-xl border border-red-200 text-red-500 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
        >
          Clôturer ma demande
        </button>
      )}

      <Modal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        title="Clôturer le ticket"
        footer={
          <>
            <Button variant="outline" onClick={() => setIsCloseModalOpen(false)}>Annuler</Button>
            <Button
              variant="primary"
              onClick={() => closeMutation.mutate()}
              isLoading={closeMutation.isPending}
              className="bg-red-500 hover:bg-red-600 text-white shadow-red-200"
            >
              Clôturer
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-300">
          Êtes-vous sûr de vouloir clôturer ce ticket ? Vous ne pourrez plus y répondre une fois fermé.
        </p>
      </Modal>
    </div>
  );
};

export default TicketDetailContent;
